<template>
  <FlotoScrollDropdown
    ref="dropdownRef"
    overlay-class-name="picker-overlay wide-picker-overlay"
    :disabled="disabled"
    v-bind="attrs"
    v-on="listeners"
    @show="handleShow"
  >
    <template v-slot:trigger="triggerSlotData">
      <MultipleTrigger
        v-if="multiple"
        :toggle="triggerSlotData.toggle"
        :allow-clear="allowClear"
        :as-input="asInput"
        :text-only="textOnly"
        :focus-event-brodcast="false"
        :selected-items="selectedItemFormMultiple"
        :disabled="disabled"
        :as-tag="asTag"
        v-bind="attrs"
        :placeholder="placeholder || $tc('custom_scope')"
        :options="availableOptions"
        @change="handleChange"
      />
    </template>
    <div class="p-2 pb-0">
      <FlotoFormItem
        ref="searchBox"
        v-model="searchTerm"
        class="search-box"
        :auto-focus="false"
        :placeholder="$t('search')"
        @keydown="handleKeyDown"
      >
        <template v-slot:prefix>
          <MIcon name="search" />
        </template>
      </FlotoFormItem>
    </div>
    <FlotoScrollView ref="menuRef">
      <div v-for="(group, key) in groupedOptions" :key="key">
        <MMenu :class="menuClass">
          <MMenuItem
            :class="{ 'cursor-default': true, [menuItemSelectedClass]: true }"
          >
            <h6 class="py-0 my-0">{{ $tc(key) }}</h6>
          </MMenuItem>
          <MMenuItem
            v-for="(item, index) in group"
            :id="`item-${item.key}`"
            :key="item.key"
            :class="{
              'scroll-dropdown-menu-item': true,
              'pointer-events-none': isOptionDisabled(value, group, item),
              [menuItemClass]: true,
              [menuItemDisabledClass]: item.disabled,
              [menuItemSelectedClass]:
                (Array.isArray(value)
                  ? value.indexOf(item.key) >= 0
                  : value === item.key) ||
                Number(focusedItem) === Number(item.key),
              'value-active-item': Array.isArray(value)
                ? value.indexOf(item.key) >= 0
                : value === item.key,
            }"
            @click="selectItem(item)"
          >
            {{ item.text }}
          </MMenuItem>
        </MMenu>
      </div>
    </FlotoScrollView>
  </FlotoScrollDropdown>
</template>

<script>
import MultipleTrigger from '@components/tree-picker/multiple-trigger'
import GroupBy from 'lodash/groupBy'
import { searchList } from '@utils/arr'
import Intersection from 'lodash/intersection'
import { getAllCustomScopeApi } from '@modules/custom-scope/custom-scope-api'
import { keyEvents } from '@utils/keyevents'

export default {
  name: 'CustomScopePicker',
  components: { MultipleTrigger },
  model: {
    event: 'change',
  },
  props: {
    disabled: { type: Boolean, default: false },
    value: {
      type: [String, Number, Array, Object, Boolean],
      default: undefined,
    },
    multiple: { type: Boolean, default: false },
    // eslint-disable-next-line
    allowClear: { type: Boolean, default: true },
    asInput: { type: Boolean, default: false },
    asTag: { type: Boolean, default: false },
    textOnly: { type: Boolean, default: false },
    placeholder: { type: String, default: undefined },
    allowSingleSelectionPerModule: { type: Boolean, default: false },
    options: { type: Array, default: () => [] },
    scope: { type: String, default: undefined },
  },
  data() {
    this.menuClass = 'ant-dropdown-menu'
    this.menuItemClass = 'ant-dropdown-menu-item'
    this.menuItemDisabledClass = 'ant-dropdown-menu-item-disabled'
    this.menuItemSelectedClass = 'ant-dropdown-menu-item-selected'
    this.unassignedValue = 0
    return {
      searchTerm: '',
      focusedItem: null, // Track the focused item
      loadedCustomScope: [],
    }
  },
  computed: {
    availableOptions() {
      return this.loadedCustomScope
    },
    groupedOptions() {
      let col = this.availableOptions
      if (this.searchTerm) {
        col = searchList(this.availableOptions, this.searchTerm, 'text')
      }
      return GroupBy(col, 'moduleName')
    },
    selectedItem() {
      if (this.availableOptions.length) {
        if (this.multiple) {
          return (this.value || []).map((id) =>
            this.availableOptions.find((o) => {
              const optionKey = o.key
              return optionKey === id
            })
          )
        }
        return (this.availableOptions || []).find((p) => {
          const optionKey = p.key
          return optionKey === this.value || optionKey === parseInt(this.value)
        })
      }
      return undefined
    },
    selectedItemFormMultiple() {
      if (this.multiple && (this.selectedItem || []).length) {
        return this.selectedItem.filter((e) => e).map((i) => i.key)
      }
      return []
    },
    listeners() {
      const { change, hide, show, click, ...listeners } = this.$listeners
      return listeners
    },
    attrs() {
      const { options, ...attrs } = this.$attrs
      return attrs
    },
  },
  watch: {
    scope: {
      handler() {
        this.getAllCusotmScope()
      },
    },
  },
  created() {
    this.getAllCusotmScope()
  },
  methods: {
    handleKeyDown(event) {
      const allOptions = this.$refs.menuRef.$el.querySelectorAll(
        '.scroll-dropdown-menu-item:not(.pointer-events-none)'
      )
      const focusedOptionIndex = Array.from(allOptions).findIndex(
        (option) => option.id === `item-${this.focusedItem}`
      )

      if (event.key === keyEvents.ARROW_DOWN) {
        // Arrow Down: Focus next item
        const nextOption = allOptions[focusedOptionIndex + 1]
        if (nextOption && nextOption.id) {
          requestAnimationFrame(() => {
            this.focusedItem = nextOption.id.replace('item-', '')
          })
        }
        this.scrollActiveItemToView()
      } else if (event.key === keyEvents.ARROW_UP) {
        // Arrow Up: Focus previous item
        const prevOption = allOptions[focusedOptionIndex - 1]
        if (prevOption && prevOption.id) {
          requestAnimationFrame(() => {
            this.focusedItem = prevOption.id.replace('item-', '')
          })
        }
        this.scrollActiveItemToView()
      } else if (event.key === keyEvents.ENTER) {
        // Enter: Select the focused item
        this.selectItem(
          (this.options || []).find(
            (option) => Number(option.key) === Number(this.focusedItem)
          )
        )
      }
    },
    isOptionDisabled(value, group, item) {
      return !this.allowSingleSelectionPerModule
        ? false
        : Intersection(
            value,
            group.map((item) => item.key)
          ).length >= 1 && value.indexOf(item.key) === -1
    },
    handleShow() {
      setTimeout(() => {
        this.$refs.searchBox && this.$refs.searchBox.focus()
        this.focusedItem = null
      }, 200)
    },
    getAllCusotmScope() {
      if (this.options && this.options.length) {
        return (this.loadedCustomScope = this.options.map((t) => ({
          ...t,
          text: t.name,
          key: t.id,
        })))
      }

      getAllCustomScopeApi({
        ...(this.scope && { scope: this.scope }),
      }).then(({ items: data }) => {
        this.loadedCustomScope = data.map((t) => ({
          ...t,
          text: t.name,
          key: t.id,
        }))
      })
    },
    selectItem(item) {
      const value = this.multiple ? this.value || [] : this.value
      if (Array.isArray(value)) {
        if (value.indexOf(item.key) === -1) {
          this.handleChange([...value, item.key])
        } else {
          this.handleChange(value.filter((i) => i !== item.key))
        }
      } else {
        this.handleChange(item.key)
        this.hide()
      }
    },
    handleChange(value) {
      if (this.multiple) {
        this.$emit(
          'selected',
          value.map((id) => this.availableOptions.find((o) => o.key === id))
        )

        this.$emit('change', value)
        this.$emit('blur')
      } else {
        this.$emit(
          'selected',
          this.availableOptions.find((o) => o.key === value)
        )
        this.$emit('change', value || this.unassignedValue)
      }
    },
    hide() {
      this.$refs.dropdownRef.hide()
    },
  },
}
</script>

<style scoped>
.pointer-events-none {
  pointer-events: none !important;
  cursor: not-allowed !important;
}
</style>
